/**
 * Shared governance workflow constants and template.
 *
 * Used by:
 * - GovernancePage setup guide (UI display + copy)
 * - POST /api/govern/setup (auto-PR creation)
 */
import type { CodebaseProfile, PackageManager } from './github-types';

export const WORKFLOW_FILENAME = 'fragments.yml';
export const WORKFLOW_PATH = `.github/workflows/${WORKFLOW_FILENAME}`;
export const GITHUB_SECRET_NAME = 'FRAGMENTS_API_KEY';
export const SETUP_BRANCH_PREFIX = 'fragments/governance-setup';
export const CONFIG_FILENAME = 'fragments.config.ts';

export interface WorkflowTemplateOptions {
  defaultBranch?: string;
  packageManager?: PackageManager | string | null;
  cloudUrl?: string;
}

export interface StarterConfigOptions {
  profile?: CodebaseProfile | null;
  componentPatterns?: string[];
}

export interface SetupPrBodyOptions {
  configCreated?: boolean;
  configAlreadyPresent?: boolean;
  profile?: CodebaseProfile | null;
}

const DEFAULT_EXCLUDES = [
  '**/node_modules/**',
  '**/dist/**',
  '**/build/**',
  '**/.next/**',
  '**/coverage/**',
  '**/*.test.ts',
  '**/*.test.tsx',
  '**/*.spec.ts',
  '**/*.spec.tsx',
];

export function buildWorkflowTemplate({
  defaultBranch = 'main',
  packageManager,
  cloudUrl,
}: WorkflowTemplateOptions = {}): string {
  const corepackStep = needsCorepack(packageManager)
    ? '\n      - run: corepack enable\n'
    : '';
  const resolvedCloudUrl = (cloudUrl ?? 'https://app.usefragments.com')
    .trim()
    .replace(/\/$/u, '');

  return `name: Fragments Governance
on:
  pull_request:
    branches: [${defaultBranch}]
  push:
    branches: [${defaultBranch}]

permissions:
  contents: read
  security-events: write

jobs:
  fragments-check:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
        with:
          fetch-depth: 0

      - uses: actions/setup-node@v4
        with:
          node-version: 20
${corepackStep}
      - run: ${installCommand(packageManager)}

      - name: Run Fragments check
        run: |
          mkdir -p .fragments
          if [ -n "$FRAGMENTS_BASE_REF" ]; then
            npx -y -p @fragments-sdk/cli fragments check --changed "origin/$FRAGMENTS_BASE_REF" --format summary --report .fragments/governance.json --sarif .fragments/governance.sarif --api-key "$FRAGMENTS_API_KEY"
          else
            npx -y -p @fragments-sdk/cli fragments check --format summary --report .fragments/governance.json --sarif .fragments/governance.sarif --api-key "$FRAGMENTS_API_KEY"
          fi
        env:
          FRAGMENTS_API_KEY: \${{ secrets.${GITHUB_SECRET_NAME} }}
          FRAGMENTS_CLOUD_URL: ${resolvedCloudUrl}
          FRAGMENTS_BASE_REF: \${{ github.base_ref }}

      - uses: actions/upload-sarif@v3
        if: always()
        with:
          sarif_file: .fragments/governance.sarif`;
}

export const WORKFLOW_TEMPLATE = buildWorkflowTemplate();

export function buildStarterFragmentsConfig({
  profile,
  componentPatterns,
}: StarterConfigOptions = {}): string {
  const components =
    componentPatterns && componentPatterns.length > 0
      ? componentPatterns
      : inferComponentPatterns(profile);
  const framework = profile?.framework.ui ?? 'react';

  return `export default {
  include: ['src/**/*.fragment.ts', 'src/**/*.fragment.tsx'],
  exclude: ${formatArray(DEFAULT_EXCLUDES, 2)},
  components: ${formatArray(components, 2)},
  framework: ${quote(framework)},

  govern: {
    presets: ['fragments/recommended'],

    styles: [
      {
        kind: 'style.rawColors.forbid',
        except: ['transparent', 'currentColor', 'inherit', 'initial', 'unset'],
        prefer: 'css-variable',
        severity: 'warn',
      },
    ],

    jsx: [
      {
        kind: 'jsx.unknownProps.forbid',
        severity: 'warn',
      },
      {
        kind: 'jsx.inlineStyle.forbidRaw',
        properties: [
          'color',
          'backgroundColor',
          'borderColor',
          'margin',
          'marginTop',
          'marginRight',
          'marginBottom',
          'marginLeft',
          'padding',
          'paddingTop',
          'paddingRight',
          'paddingBottom',
          'paddingLeft',
          'gap',
        ],
        severity: 'warn',
      },
    ],

    agent: {
      repairOrder: [
        'apply-deterministic-fixes',
        'replace-raw-styles-with-tokens',
        'ask-model-for-semantic-a11y-text',
      ],
    },
  },
};
`;
}

export function buildSetupBranch(timestamp?: number): string {
  const ts = timestamp ?? Date.now();
  return `${SETUP_BRANCH_PREFIX}-${ts}`;
}

export function buildPrBody(options: SetupPrBodyOptions = {}): string {
  const detected = detectedSummary(options.profile);
  const configLine = options.configCreated
    ? `- \`${CONFIG_FILENAME}\` — starter governance config generated from repo scan signals`
    : options.configAlreadyPresent
      ? `- Existing \`${CONFIG_FILENAME}\` left unchanged as the source of truth`
      : `- \`${CONFIG_FILENAME}\` — repo-owned governance config`;

  return [
    '## Fragments Governance CI',
    '',
    'This PR adds automated governance checks to your CI pipeline.',
    '',
    "**What's included:**",
    `- \`${WORKFLOW_PATH}\` — GitHub Actions workflow that runs \`fragments check\` on every push and PR`,
    configLine,
    '',
    '**What gets checked:**',
    '- **Style governance** — raw colors and inline style values that should use your design system',
    '- **Accessibility** — required accessible names and semantic UI contracts when configured',
    '- **Component usage** — prop and component governance when fragments declare those rules',
    ...(detected.length > 0 ? ['', '**Detected baseline:**', ...detected] : []),
    '',
    '**Review before merging:**',
    '- Confirm the component globs match the code you want governed',
    '- Adjust severities if governance should be advisory before it blocks PRs',
    '- Add spacing or type scales only if they reflect real design tokens',
    '',
    '**Policy management:**',
    `After this PR merges, \`${CONFIG_FILENAME}\` is the source of truth. Fragments Cloud displays findings and setup status from the repo-owned config.`,
    '',
    'Results are reported to Fragments Cloud automatically.',
    '',
    '---',
    '*Opened by Fragments Cloud*',
  ].join('\n');
}

function installCommand(
  packageManager: WorkflowTemplateOptions['packageManager'],
): string {
  switch (packageManager) {
    case 'pnpm':
      return 'pnpm install --frozen-lockfile';
    case 'yarn':
      return 'yarn install --frozen-lockfile';
    case 'bun':
      return 'bun install';
    default:
      return 'npm install';
  }
}

function needsCorepack(
  packageManager: WorkflowTemplateOptions['packageManager'],
): boolean {
  return packageManager === 'pnpm' || packageManager === 'yarn';
}

function inferComponentPatterns(profile?: CodebaseProfile | null): string[] {
  const roots = profile?.componentRoots
    ?.filter((root) => root.path && root.componentCount > 0)
    .sort(
      (a, b) =>
        Number(b.looksLikeDesignSystem) - Number(a.looksLikeDesignSystem),
    )
    .slice(0, 4)
    .map((root) => `${trimSlashes(root.path)}/**/*.tsx`);
  return unique(roots && roots.length > 0 ? roots : ['src/**/*.tsx']);
}

function trimSlashes(path: string): string {
  return path.replace(/^\/+|\/+$/gu, '');
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}

function quote(value: string): string {
  return `'${value.replace(/\\/gu, '\\\\').replace(/'/gu, "\\'")}'`;
}

function formatArray(values: string[], indent: number): string {
  if (values.length === 0) return '[]';
  const pad = ' '.repeat(indent);
  const itemPad = ' '.repeat(indent + 2);
  return `[\n${values.map((value) => `${itemPad}${quote(value)},`).join('\n')}\n${pad}]`;
}

function detectedSummary(profile?: CodebaseProfile | null): string[] {
  if (!profile) return [];
  const lines = [
    `- Framework: \`${profile.framework.meta ?? profile.framework.ui}\``,
    `- Styling: \`${profile.styling.primary}\``,
    `- Package manager: \`${profile.packageManager}\``,
  ];
  if (profile.tokens.totalCount > 0) {
    lines.push(
      `- Tokens: \`${profile.tokens.totalCount}\` from \`${profile.tokens.format}\``,
    );
  }
  return lines;
}
